// Stage options for the schedule PDF export's "which stages" filter.
//
// The stage key ("id:<uuid>" / "name:<label>") is the canonical
// `stageGroupKey` from @produce/shared — the same identity the on-screen
// schedule uses — sent verbatim to export-schedule-pdf as `selectedStageKeys`.
// The edge function's Deno lib.ts mirrors that exact format (it can't import
// the web bundle); both sides are unit-tested against it.
import { groupByStage, stageGroupKey, type StageBoundRow } from "@produce/shared";

export type StageRow = StageBoundRow;

export interface StageOption {
  key: string;
  label: string;
}

export interface DayStageOptions {
  day: string;
  options: StageOption[];
}

// FK-first identity, name fallback — re-exported canonical key.
export const stageOptionKey = stageGroupKey;

// Distinct stages present in the rows, in first-seen order. Empty rows → no
// options (the filter UI then hides itself).
export function stageOptionsFromRows(rows: StageRow[]): StageOption[] {
  return groupByStage(rows).map((g) => ({ key: g.key, label: g.label }));
}

// Stage options grouped per day, in the given day order. A multi-day festival
// stores one stage row per day (distinct stage_id), so a flat list shows the
// same label N times with no day context — grouping makes the producer's "pick
// stages per day" choice unambiguous. Days with no stages are dropped.
export function stageOptionsByDay(
  rows: (StageRow & { event_date?: string | null })[],
  days: string[],
): DayStageOptions[] {
  return days
    .map((day) => ({
      day,
      options: stageOptionsFromRows(rows.filter((r) => r.event_date === day)),
    }))
    .filter((d) => d.options.length > 0);
}
